// PWA Service - Service Worker Registration, Sync & Notifications
import { 
  initDB, 
  getOfflineQueue, 
  removeFromOfflineQueue, 
  addToOfflineQueue,
  cachePatients,
  cacheMedicationSchedules,
  getSetting,
  setSetting
} from './offlineDB';
import api from './api';

// Check if online
export const isOnline = () => navigator.onLine;

// Register Service Worker
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('[PWA] Service Worker registered:', registration.scope);
      
      // Listen for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        newWorker?.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New version available
            window.dispatchEvent(new CustomEvent('sw-update-available'));
          }
        });
      });
      
      return registration;
    } catch (error) {
      console.error('[PWA] Service Worker registration failed:', error);
      return null;
    }
  }
  return null;
};

// Skip waiting and activate new service worker
export const updateServiceWorker = async () => {
  const registration = await navigator.serviceWorker.ready;
  registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
};

// Listen for messages from service worker
export const setupServiceWorkerMessageListener = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', async (event) => {
      if (event.data.type === 'QUEUE_OFFLINE_REQUEST') {
        await addToOfflineQueue(event.data.data);
      }
      if (event.data.type === 'SYNC_OFFLINE_DATA') {
        await syncOfflineData();
      }
    });
  }
};

// Sync offline data when back online
export const syncOfflineData = async () => {
  if (!isOnline()) return { synced: 0, failed: 0 };
  
  const queue = await getOfflineQueue();
  let synced = 0;
  let failed = 0;
  
  for (const item of queue) {
    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: item.headers,
        body: item.body
      });
      
      if (response.ok) {
        await removeFromOfflineQueue(item.id);
        synced++;
      } else {
        failed++;
      }
    } catch (error) {
      console.error('[PWA] Sync failed for item:', item.id, error);
      failed++;
    }
  }
  
  return { synced, failed };
};

// Cache essential data for offline use
export const cacheDataForOffline = async () => {
  try {
    // Cache patients
    const patientsRes = await api.get('/patients');
    if (patientsRes.data) {
      await cachePatients(patientsRes.data);
    }
    
    // Cache medication schedules
    const medsRes = await api.get('/medication-schedules');
    if (medsRes.data) {
      await cacheMedicationSchedules(medsRes.data);
    }
    
    console.log('[PWA] Data cached for offline use');
    return true;
  } catch (error) {
    console.error('[PWA] Failed to cache data:', error);
    return false;
  }
};

// ============================================
// PUSH NOTIFICATIONS
// ============================================

// Check if notifications are supported
export const notificationsSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!notificationsSupported()) return 'unsupported';
  
  const permission = await Notification.requestPermission();
  await setSetting('notifications_permission', permission);
  return permission;
};

// Get current notification permission
export const getNotificationPermission = () => {
  if (!notificationsSupported()) return 'unsupported';
  return Notification.permission;
};

// Check if notifications are enabled in settings
export const areNotificationsEnabled = async () => {
  const enabled = await getSetting('notifications_enabled');
  return enabled !== false; // Default to true
};

// Enable/disable notifications
export const setNotificationsEnabled = async (enabled) => {
  await setSetting('notifications_enabled', enabled);
};

// Schedule a medication notification
export const scheduleMedicationNotification = async (schedule) => {
  const enabled = await areNotificationsEnabled();
  if (!enabled || getNotificationPermission() !== 'granted') return;
  
  const registration = await navigator.serviceWorker.ready;
  
  // Calculate time until notification
  const now = new Date();
  const nextDose = new Date(schedule.next_dose_time);
  const delay = nextDose.getTime() - now.getTime();
  
  if (delay <= 0) return; // Already passed
  
  // Use setTimeout for now (in production, use Push API with server)
  setTimeout(() => {
    showLocalNotification(registration, {
      title: `💊 Medicación: ${schedule.patient_name}`,
      body: `${schedule.medication_name} - ${schedule.dosage}\nVía: ${schedule.route}`,
      tag: `med-${schedule.id}`,
      data: { url: `/patients/${schedule.patient_id}`, scheduleId: schedule.id }
    });
  }, Math.min(delay, 2147483647)); // Max setTimeout value
};

// Show a local notification
export const showLocalNotification = async (registration, options) => {
  const enabled = await areNotificationsEnabled();
  if (!enabled) return;
  
  if (registration) {
    await registration.showNotification(options.title, {
      body: options.body,
      icon: '/logo.png',
      badge: '/logo.png',
      tag: options.tag,
      data: options.data,
      requireInteraction: true,
      vibrate: [200, 100, 200]
    });
  } else if (notificationsSupported() && Notification.permission === 'granted') {
    new Notification(options.title, {
      body: options.body,
      icon: '/logo.png',
      tag: options.tag
    });
  }
};

// Schedule all pending medication notifications
export const scheduleAllMedicationNotifications = async (schedules) => {
  for (const schedule of schedules) {
    if (schedule.status === 'active') {
      await scheduleMedicationNotification(schedule);
    }
  }
};

// ============================================
// ONLINE/OFFLINE EVENT HANDLERS
// ============================================

export const setupOnlineOfflineHandlers = (onOnline, onOffline) => {
  window.addEventListener('online', async () => {
    console.log('[PWA] Back online');
    const result = await syncOfflineData();
    if (onOnline) onOnline(result);
  });
  
  window.addEventListener('offline', () => {
    console.log('[PWA] Gone offline');
    if (onOffline) onOffline();
  });
};

// ============================================
// PWA INSTALL PROMPT
// ============================================

let deferredPrompt = null;

export const setupInstallPrompt = () => {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new CustomEvent('pwa-install-available'));
  });
};

export const canInstallPWA = () => deferredPrompt !== null;

export const installPWA = async () => {
  if (!deferredPrompt) return false;
  
  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  
  return outcome === 'accepted';
};

export const isPWAInstalled = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
};

// ============================================
// INITIALIZATION
// ============================================

export const initializePWA = async () => {
  await initDB();
  await registerServiceWorker();
  setupServiceWorkerMessageListener();
  setupInstallPrompt();
  
  // Cache data if online
  if (isOnline()) {
    await cacheDataForOffline();
  }
  
  return {
    isOnline: isOnline(),
    notificationsSupported: notificationsSupported(),
    notificationPermission: getNotificationPermission(),
    isInstalled: isPWAInstalled()
  };
};
